import { BrowserRouter, Routes, Route, useLocation, Navigate } from "react-router-dom";
import { Toaster } from "sonner";
import { Loader2 } from "lucide-react";

import "@/App.css";
import { AuthProvider, useAuth } from "./lib/auth-context";
import SignIn from "./pages/SignIn";
import SignUp from "./pages/SignUp";
import AuthCallback from "./pages/AuthCallback";
import Onboarding from "./pages/Onboarding";
import Dashboard from "./pages/Dashboard";
import Candidates from "./pages/Candidates";
import CandidateDetail from "./pages/CandidateDetail";
import Roles from "./pages/Roles";
import RoleDetail from "./pages/RoleDetail";
import RoleScreening from "./pages/RoleScreening";
import CandidateComparison from "./pages/CandidateComparison";
import Interviews from "./pages/Interviews";
import InterviewDetail from "./pages/InterviewDetail";
import Usage from "./pages/Usage";
import Search from "./pages/Search";
import Team from "./pages/Team";
import Settings from "./pages/Settings";
import Forbidden from "./pages/Forbidden";
import AcceptInvite from "./pages/AcceptInvite";
import ProtectedRoute from "./components/ProtectedRoute";

// Root route: pure auth-status gate. No landing page.
function RootRedirect() {
  const { user, member, loading } = useAuth();
  if (loading) {
    return (
      <div
        className="flex h-screen w-full items-center justify-center bg-ink-50"
        data-testid="root-loading"
      >
        <Loader2 className="h-5 w-5 animate-spin text-ink-400" strokeWidth={1.5} />
      </div>
    );
  }
  if (!user) return <Navigate to="/login" replace />;
  if (!member) return <Navigate to="/onboarding" replace />;
  // Step B — HM's home is their assigned-interviews list (no dashboard).
  if (member.role === "hiring_manager") return <Navigate to="/interviews" replace />;
  return <Navigate to="/dashboard" replace />;
}

function AppRouter() {
  const location = useLocation();
  // Detect Emergent OAuth callback synchronously during render (NOT useEffect)
  // to prevent race conditions with AuthProvider.
  if (location.hash?.includes("session_id=")) {
    return <AuthCallback />;
  }
  return (
    <Routes>
      <Route path="/" element={<RootRedirect />} />
      {/* Public auth routes */}
      <Route path="/login" element={<SignIn />} />
      <Route path="/signup" element={<SignUp />} />
      <Route path="/auth/callback" element={<AuthCallback />} />
      <Route path="/invite/:token" element={<AcceptInvite />} />
      <Route path="/forbidden" element={<Forbidden />} />
      {/* Legacy aliases — keep /signin working as redirect to /login */}
      <Route path="/signin" element={<Navigate to="/login" replace />} />
      {/* Authenticated app */}
      <Route
        path="/onboarding"
        element={
          <ProtectedRoute requireWorkspace={false}>
            <Onboarding />
          </ProtectedRoute>
        }
      />
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Dashboard />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Candidates />
          </ProtectedRoute>
        }
      />
      <Route
        path="/candidates/:candidateId"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <CandidateDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Roles />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/:roleId"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <RoleDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/:roleId/compare"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <CandidateComparison />
          </ProtectedRoute>
        }
      />
      <Route
        path="/roles/:roleId/screen"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <RoleScreening />
          </ProtectedRoute>
        }
      />
      <Route
        path="/search"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Search />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews"
        element={
          <ProtectedRoute>
            <Interviews />
          </ProtectedRoute>
        }
      />
      <Route
        path="/interviews/:interviewId"
        element={
          <ProtectedRoute>
            <InterviewDetail />
          </ProtectedRoute>
        }
      />
      <Route
        path="/usage"
        element={
          <ProtectedRoute requireRole="admin">
            <Usage />
          </ProtectedRoute>
        }
      />
      <Route
        path="/team"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Team />
          </ProtectedRoute>
        }
      />
      <Route
        path="/settings"
        element={
          <ProtectedRoute denyRoles={["hiring_manager"]}>
            <Settings />
          </ProtectedRoute>
        }
      />
      {/* Anything else -> root, which gates on auth */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}

function App() {
  return (
    <AuthProvider>
      <BrowserRouter>
        <AppRouter />
        <Toaster
          position="bottom-right"
          toastOptions={{
            className:
              "bg-white border border-ink-200 text-ink-900 text-[13px] shadow-sm",
          }}
        />
      </BrowserRouter>
    </AuthProvider>
  );
}

export default App;
